import { type CSSProperties } from 'react';

import {
    detectOverflow,
    shift,
    type Middleware,
    type OffsetOptions,
    type Placement,
    type Side
} from '@floating-ui/react';

import { FLOATING_ARROW_HEIGHT, FLOATING_ARROW_WIDTH } from './Floating.constants';

import type { FloatingAlignConfig, FloatingOptions } from './Floating.types';

// adjusts the arrow position to match our design
export function getFixedArrowPositionStyles(placement: Placement) {
    const horizontalSpacing = 'var(--spacing-6)';
    const verticalSpacing = 'var(--spacing-4)';
    const startHorizontal = { left: horizontalSpacing };
    const endHorizontal = { right: horizontalSpacing, left: 'auto' };
    const startVertical = { top: verticalSpacing };
    const endVertical = { bottom: verticalSpacing, top: 'auto' };
    const placementStyles: Partial<Record<Placement, CSSProperties>> = {
        'top-start': startHorizontal,
        'top-end': endHorizontal,
        'right-start': startVertical,
        'right-end': endVertical,
        'bottom-start': startHorizontal,
        'bottom-end': endHorizontal,
        'left-start': startVertical,
        'left-end': endVertical
    };
    return (placementStyles[placement as never] ?? {}) as CSSProperties;
}

/**
 * Combines event handlers from two props objects, ensuring both handlers are called
 * with child handlers firing before base handlers.
 */
export function combineEventHandlers<T extends Record<string, any>>(
    baseProps: T,
    childProps: Partial<T>
): T {
    return Object.keys(baseProps)
        .filter((key) => key.startsWith('on') && typeof baseProps[key] === 'function')
        .reduce(
            (acc, eventName) => ({
                ...acc,
                [eventName]: (...args: any[]) => {
                    const childHandler = childProps?.[eventName];
                    const baseHandler = baseProps[eventName];
                    if (typeof childHandler === 'function') {
                        childHandler(...args);
                    }
                    if (typeof baseHandler === 'function') {
                        baseHandler(...args);
                    }
                }
            }),
            {}
        ) as T;
}

/**
 * Parses the offset value for floating elements.
 * If the offset is a number, it is returned directly.
 * If it is an array, it is converted to an object with mainAxis and crossAxis properties.
 * If it is undefined or not an array, a fallback value is returned.
 */
export function parseOffset(
    offsetValue: FloatingAlignConfig['offset'] | number | undefined,
    fallbackValue = 16
): OffsetOptions {
    if (typeof offsetValue === 'number') {
        return offsetValue;
    }
    if (!Array.isArray(offsetValue)) {
        return fallbackValue;
    }
    const [mainAxis, crossAxis] = offsetValue;
    return {
        mainAxis: mainAxis ?? fallbackValue,
        crossAxis: crossAxis ?? fallbackValue
    };
}

// Adjusts the origin of the animation based on the arrow position
// to make it look like the popup is "growing" from the arrow position.
// https://floating-ui.com/docs/useTransition#scale-transforms
export function getTransformOrigin(side: Side, arrowX: number, arrowY: number) {
    if (!arrowX && !arrowY) {
        // If the arrow isn’t available, use a simpler method
        // to maintain the directional effect.
        return {
            top: 'center bottom',
            bottom: 'center top',
            left: 'right center',
            right: 'left center'
        }[side];
    }
    const transformX = arrowX + FLOATING_ARROW_WIDTH / 2;
    const transformY = arrowY + FLOATING_ARROW_HEIGHT;
    const growingOffset = FLOATING_ARROW_HEIGHT + 16; // adds extra growing effect
    return {
        top: `${transformX}px calc(100% + ${growingOffset}px)`,
        bottom: `${transformX}px ${-growingOffset}px`,
        left: `calc(100% + ${growingOffset}px) ${transformY}px`,
        right: `${-growingOffset}px ${transformY}px`
    }[side];
}

/** * Middleware to keep the floating element sticky on top while scrolling within a specified container.
 * It checks the overflow of the container and adjusts the y position of the floating element accordingly.
 * If the top overflow is greater than 0, it sets the y position to the top of the container.
 * @param options.ref - The container element that is being scrolled.
 * @returns Middleware that modifies the y position of the floating element based on the scrollable container's overflow.
 */
export function stickyOnTopWhileScrolling(
    options: NonNullable<FloatingOptions['stickyOnTopWhileScrolling']>
): Middleware {
    return {
        name: 'stickyOnTopWhileScrolling',
        async fn(state) {
            const overflow = await detectOverflow(state, {
                altBoundary: true
            });
            let normalizedConfig =
                typeof options === 'boolean' ? { ref: null, offset: 0 } : options;
            if ('current' in normalizedConfig) {
                normalizedConfig = {
                    ref: normalizedConfig,
                    offset: 0
                };
            }
            const scrollableContainer = normalizedConfig.ref?.current ?? document.body;
            const offset = normalizedConfig.offset ?? 0;
            const containerRect = scrollableContainer.getBoundingClientRect();
            const fixed = overflow.top > 0;
            const topOffset = fixed ? Math.max(containerRect.top + offset, offset) : state.y;
            return {
                y: topOffset,
                data: {
                    fixed
                }
            };
        }
    };
}

export const shiftPopperWhenCloseToViewportEdge = (padding: number): Middleware => {
    return shift({
        padding
    });
};
